
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, effect, inject, input } from '@angular/core';
import { AbstractControl, FormGroupDirective, ValidationErrors } from '@angular/forms';
import { merge } from 'rxjs';

export type ErrorMessageMapper = Record<string, string | ((value: unknown) => string)>;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  selector: 'cfi-form-inline-error',
  imports: [],
  templateUrl: './form-inline-error.component.html',
  styleUrl: './form-inline-error.component.scss'
})
export class FormInlineErrorComponent implements DoCheck {
  public control = input.required<AbstractControl | null>();
  public customErrorMessages = input<ErrorMessageMapper>();

  private readonly cdr = inject(ChangeDetectorRef);
  // We use FormGroupDirective to determine if the form has been submitted, which is important for showing errors on untouched fields after submission.
  private readonly formGroupDirective = inject(FormGroupDirective, { optional: true });
  private lastInteractionState = ''; // Format: touched|dirty|submitted|hasErrors

  constructor() {
    effect((onCleanup) => {
      const ctrl = this.control();
      if (!ctrl) {
        return;
      }

      const sub = merge(ctrl.statusChanges, ctrl.valueChanges).subscribe(() => {
        this.cdr.markForCheck();
      });
      onCleanup(() => sub.unsubscribe());
    });
  }

  // We implement DoCheck to detect changes in the control's interaction state (touched, dirty, submitted, hasErrors) 
  // and trigger change detection when it changes.
  public ngDoCheck(): void {
    const ctrl = this.control();
    const submitted = !!this.formGroupDirective?.submitted;
    const nextState = `${ctrl?.touched ?? false}|${ctrl?.dirty ?? false}|${submitted}|${!!ctrl?.errors}`;

    if (nextState !== this.lastInteractionState) {
      this.lastInteractionState = nextState;
      this.cdr.markForCheck();
    }
  }

  /**
   * Gets all validation errors from the control
   */
  public getErrors(): ValidationErrors | null {
    const ctrl = this.control();
    if (!ctrl) {
      return null;
    }
    if (!this.shouldShowErrors(ctrl)) {
      return null;
    }
    return ctrl.errors;
  }

  // Determines if the control should show errors based on its interaction state (touched, dirty, or form submitted)
  private shouldShowErrors(ctrl: AbstractControl): boolean {
    return ctrl.touched || ctrl.dirty || !!this.formGroupDirective?.submitted;
  }

  /**
   * Formats error key to human-readable message
   */
  public formatErrorMessage(errorKey: string, errorValue: unknown): string {
    // Check for custom error messages first
    const customMessages = this.customErrorMessages();
    if (customMessages?.[errorKey]) {
      const customMessage = customMessages[errorKey];
      if (typeof customMessage === 'function') {
        return customMessage(errorValue);
      }
      return customMessage;
    }

    // Fall back to default error messages
    const errorMessages: Record<string, (value: unknown) => string> = {
      required: () => 'This field is required',
      email: () => 'Please enter a valid email address',
      invalidEmail: () => 'Please enter a valid email address',
      minlength: (value) => `Minimum length is ${(value as { requiredLength: number }).requiredLength} characters`,
      maxlength: (value) => `Maximum length is ${(value as { requiredLength: number }).requiredLength} characters`,
      min: (value) => `Minimum value is ${(value as { min: number }).min}`,
      max: (value) => `Maximum value is ${(value as { max: number }).max}`,
      pattern: () => 'Please enter a valid format',
      matDatepickerParse: () => 'Date is invalid',
      futureDate: () => 'Date must be in the past',
      invalidSSN: () => 'Please enter a valid SSN',
      invalidNumber: () => 'Please enter a valid number',
      invalidZipCode: () => 'Please enter a valid ZIP code',
      whitespaceOnly: () => 'This field cannot be only whitespace',
      invalidUrl: () => 'Please enter a valid URL',
      invalidPhone: () => 'Please enter a valid phone number',
      invalidFEIN: () => 'Please enter a valid number',
      invalidHHSize: () => 'Please enter a valid Household Size',
      invalidTenDigitNumber: () => 'Please enter a valid 10-digit number'
    };

    if (errorMessages[errorKey]) {
      return errorMessages[errorKey](errorValue);
    }

    // For custom validators, try to use error message if provided
    if (errorValue && typeof errorValue === 'object' && 'message' in errorValue) {
      return (errorValue as { message: string }).message;
    }

    // Fallback: convert camelCase to Title Case
    return errorKey.replaceAll(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  }

  /**
   * Gets error entries as key-value pairs.
   * When the datepicker cannot parse input, hide `required` so users see an invalid-date
   * message instead of both required and parse errors.
   */
  public getErrorEntries(): Array<[string, unknown]> {
    const ctrl = this.control();
    if (!ctrl || !this.shouldShowErrors(ctrl)) {
      return [];
    }

    const errors = ctrl?.errors;
    if (!errors) {
      return [];
    }

    const entries = Object.entries(errors);
    if (errors['matDatepickerParse']) {
      return entries.filter(([key]) => key !== 'required');
    }

    // For certain errors, we want to hide mask, pattern, and format errors to avoid overwhelming the user with multiple messages.
    if (errors['invalidPhone'] || errors['invalidZipCode'] || errors['invalidFEIN'] || errors['invalidSSN'] || errors['invalidNumber'] || errors['invalidHHSize']) {
      return entries.filter(([key]) => key !== 'mask' && key !== 'pattern' && key !== 'format');
    }

    return entries;
  }
}
